/**
 * Build a locally-constructed UserMediaEntity for a just-uploaded video.
 *
 * Called after `publishContentUseCase.execute()` reports success. The entity
 * is inserted into the owner's grid with the "Processing" overlay until the
 * transcode cloud function finishes and the Firestore listener brings in the
 * server-side doc.
 *
 * Mirrors Flutter's add_content_screen.dart:466-479, minus the thumbnail
 * (the overlay covers the surface color; no local thumbnail is rendered).
 */

import type { UserMediaEntity } from '@/domain/entities/userMedia/userMediaEntity';
import type { PublishContentRequest } from '@/domain/entities/publish/publishContentRequest';
import { MediaType } from '@/domain/enums/mediaType';
import { VIDEO_CONSTANTS } from '@/domain/constants/videoConstants';

export function buildLocalUserMedia(
  request: PublishContentRequest,
  shareLink: string,
): UserMediaEntity {
  const {
    mediaId,
    videoMeta,
    descriptionResult,
    contentSettings,
    monetizationResult,
    userName,
    firebaseUserId,
  } = request;

  const aspectRatio =
    videoMeta.width > 0 && videoMeta.height > 0
      ? videoMeta.width / videoMeta.height
      : VIDEO_CONSTANTS.DEFAULT_ASPECT_RATIO;

  const now = Date.now();

  return {
    id: mediaId,
    firebaseId: firebaseUserId,
    createdTimestamp: now,
    updatedTimestamp: now,
    isProcessing: true,
    isLocal: true,
    type: MediaType.VIDEO,
    relativePath: '',
    aspectRatio,
    duration: videoMeta.duration * 1000,
    userName: userName ?? undefined,
    description: descriptionResult.description,
    hashTags: descriptionResult.hashTags ?? [],
    mentions: descriptionResult.mentions ?? [],
    contentLink: contentSettings.contentLink ?? undefined,
    isDraft: contentSettings.isDraft,
    allowComments: contentSettings.allowComments,
    showCoins: contentSettings.showCoins,
    allowUnlock: monetizationResult.allowUnlock,
    unlockCharge: monetizationResult.unlockCharge ?? undefined,
    schedule: contentSettings.schedule ?? undefined,
    allowEarlyAccess: contentSettings.allowEarlyAccess,
    shareLink,
    reports: 0,
    blocked: false,
    isStartUp: false,
    isContent: true,
  };
}
